import { useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PLANS = [
  {
    lookup_key: "journal_pro_monthly",
    label: "Monthly",
    price: "$9",
    unit: "/ month",
    tagline: "Try it out.",
    features: ["Unlimited AI coaching threads", "Unlimited prop-firm accounts", "Weekly digest email", "Cancel anytime"],
    highlight: false,
  },
  {
    lookup_key: "journal_pro_annual",
    label: "Annual",
    price: "$79",
    unit: "/ year",
    tagline: "Save 27% — 2 months free.",
    features: ["Everything in Monthly", "Priority AI response", "Founding-member badge on your profile", "Locked-in early pricing"],
    highlight: true,
  },
  {
    lookup_key: "journal_pro_lifetime",
    label: "Lifetime",
    price: "$199",
    unit: "one-time",
    tagline: "Pay once. Own it forever.",
    features: ["All future features included", "No renewal, ever", "First access to new AI models", "Founding-member badge + lifetime crest"],
    highlight: false,
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null); // lookup_key being processed
  const [error, setError] = useState(null);

  const startCheckout = async (lookup_key) => {
    setError(null);
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setLoading(null);
    }
  };

  return (
    <Layout>
      <div className="mb-10 fade-in max-w-3xl">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CFA66A] mb-2">— Unlock Pro</p>
        <h1 className="font-head text-4xl sm:text-5xl font-bold text-[#EBE5D9]">Choose your edge</h1>
        <p className="text-[#A39A8E] mt-4 text-base">
          Journal Pro gives you unlimited AI coaching, unlimited prop-firm accounts, and a weekly digest emailed to you every Monday. Stay sharp between sessions.
        </p>
        {user?.is_pro && (
          <p data-testid="already-pro-badge" className="mt-4 inline-block font-mono text-[10px] uppercase tracking-widest text-[#CFA66A] border border-[#CFA66A] px-3 py-1 rounded-sm">
            You&apos;re Pro · {user.pro_type}
          </p>
        )}
      </div>

      {error && <p data-testid="pricing-error" className="mb-4 font-mono text-sm text-[#B56258]">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PLANS.map((p) => (
          <div
            key={p.lookup_key}
            data-testid={`plan-card-${p.lookup_key}`}
            className={`border p-6 rounded-sm relative flex flex-col ${
              p.highlight
                ? "border-[#CFA66A] bg-[#CFA66A]/5"
                : "border-[#2E2A25] bg-[#1A1815]"
            }`}
          >
            {p.highlight && (
              <span className="absolute -top-3 left-6 font-mono text-[9px] uppercase tracking-widest bg-[#CFA66A] text-[#0F0E0C] px-2 py-1 rounded-sm">
                Best value
              </span>
            )}
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">{p.label}</p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-head text-4xl text-[#EBE5D9]">{p.price}</span>
              <span className="font-mono text-xs text-[#A39A8E]">{p.unit}</span>
            </div>
            <p className="mt-1 text-sm text-[#A39A8E]">{p.tagline}</p>
            <ul className="mt-6 space-y-2 flex-1">
              {p.features.map((f) => (
                <li key={f} className="text-sm text-[#EBE5D9] flex gap-2">
                  <span className="text-[#CFA66A]">›</span>{f}
                </li>
              ))}
            </ul>
            <button
              data-testid={`plan-buy-${p.lookup_key}`}
              onClick={() => startCheckout(p.lookup_key)}
              disabled={loading === p.lookup_key || user?.is_pro}
              className={`mt-6 w-full py-2.5 rounded-sm font-medium transition-colors font-mono text-xs uppercase tracking-widest disabled:opacity-50 ${
                p.highlight
                  ? "bg-[#CFA66A] text-[#0F0E0C] hover:bg-[#EBE5D9]"
                  : "bg-transparent border border-[#CFA66A] text-[#CFA66A] hover:bg-[#CFA66A]/10"
              }`}
            >
              {user?.is_pro ? "Already Pro" : loading === p.lookup_key ? "Redirecting…" : `Get ${p.label}`}
            </button>
          </div>
        ))}
      </div>

      <p className="mt-8 font-mono text-[10px] text-[#A39A8E] uppercase tracking-widest">
        Sandbox mode · Test card 4242 4242 4242 4242, any future expiry, any CVC.
      </p>
    </Layout>
  );
}
